// The bar over Needed from Amazon's list once items are ticked: assign them all to one
// person, to InRange, or clear them, and (for Amazon's people) mark them all received with one
// note. Each change is checked item by item; what was left as it was says why
// (docs/Y2026-Q4/design-amazon-item-owners.md section 2.9).
import { useCallback, useRef, useState } from "react";

import {
  type Assignee,
  type BulkResult,
  INRANGE,
  itemId,
  useBulkAssign,
  useBulkResolve,
} from "../../api/hooks/useAmazonOwners";
import { NoteDialog } from "../../components/NoteDialog";
import { type ItemRef, OwnerMenu, useOwners } from "./ItemOwners";
import { useDismiss } from "../../utils/useDismiss";
import { useReadOnly } from "./ViewAs";
import styles from "./itemOwners.module.css";

type Pending =
  | { kind: "person"; assignee: string }
  | { kind: "inrange" }
  | { kind: "clear" }
  | { kind: "resolved" };

/** The most items one change takes (amazon_overview._items.BULK_MAX). */
const BULK_MAX = 300;

const items = (n: number): string => `${n} item${n === 1 ? "" : "s"}`;

const outcome = (result: BulkResult, did: string): string => {
  const left = result.skipped.length;
  if (left === 0) return `${did} ${items(result.done.length)}.`;
  const reasons = [...new Set(result.skipped.map((s) => s.reason))].join(" ");
  return `${did} ${items(result.done.length)}. ${items(left)} not changed: ${reasons}`;
};

export const BulkBar = ({
  selected,
  onDone,
  onClear,
}: {
  /** The ticked items still in view, which the actions act on. */
  selected: ItemRef[];
  /** Untick the items that changed. */
  onDone: (changed: Set<string>) => void;
  onClear: () => void;
}) => {
  const o = useOwners();
  const readOnly = useReadOnly();
  const assign = useBulkAssign();
  const resolve = useBulkResolve();
  const [menu, setMenu] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenu(false), []);
  useDismiss(menu, closeMenu, ref);

  if (selected.length === 0 && !said) return null;
  // Each item's expected owner is read from the owners: unloaded, every one would read as
  // nobody's, and each item someone owns would be refused as changed meanwhile.
  const ready = !!o.owners;
  const tooMany = selected.length > BULK_MAX;
  const busy = assign.isLoading || resolve.isLoading;
  const ids = selected.map((i) => ({ project_id: i.projectId, item_key: i.itemKey }));
  const finished = (did: string) => (result: BulkResult) => {
    setPending(null);
    setSaid(outcome(result, did));
    onDone(new Set(result.done.map((d) => itemId(d.project_id, d.item_key))));
  };
  const sendAssign = (assignee: Assignee, reason?: string) =>
    assign.mutate(
      {
        items: ids.map((id, n) => ({ ...id, expected: o.ownerOf(selected[n]) })),
        assignee,
        reason,
      },
      {
        onSuccess: finished(
          assignee === null
            ? "Cleared the owner of"
            : assignee === INRANGE
              ? "Assigned to InRange:"
              : `Assigned to ${o.nameOf(assignee)}:`,
        ),
      },
    );
  const choose = (next: Pending) => {
    setMenu(false);
    setSaid(null);
    assign.reset();
    resolve.reset();
    setPending(next);
  };
  const what = items(selected.length);
  const error = (assign.error ?? resolve.error)?.message ?? null;
  const dialogBase = {
    open: pending !== null,
    isSubmitting: busy,
    itemName: what,
    onCancel: () => setPending(null),
    submitError: error,
    placeholder: "",
  };

  return (
    <div className={styles.bulkBar} role="region" aria-label="Bulk actions">
      {selected.length > 0 && (
        <>
          <strong>{what} selected</strong>
          <div className={styles.chipRoot} ref={ref}>
            <button
              type="button"
              className={styles.ghost}
              aria-haspopup="menu"
              aria-expanded={menu}
              disabled={!!readOnly || busy || !ready || tooMany}
              title={readOnly ?? undefined}
              onClick={() => setMenu(!menu)}
            >
              Assign to…
            </button>
            {menu && (
              <OwnerMenu
                current={undefined}
                onPick={(assignee) =>
                  choose(
                    assignee === null
                      ? { kind: "clear" }
                      : assignee === INRANGE
                        ? { kind: "inrange" }
                        : { kind: "person", assignee },
                  )
                }
                foot="Recorded in each item's history; one Slack message lists them."
              />
            )}
          </div>
          {!o.isStaff && (
            <button
              type="button"
              className={styles.solid}
              disabled={!!readOnly || busy || !ready || tooMany}
              title={readOnly ?? undefined}
              onClick={() => choose({ kind: "resolved" })}
            >
              Mark as received
            </button>
          )}
          <button type="button" className={styles.link} onClick={onClear}>
            Clear selection
          </button>
          {tooMany && (
            <span className={styles.error}>
              At most {BULK_MAX} items at a time. Narrow the list or untick some.
            </span>
          )}
          {!ready && o.failed && (
            <span className={styles.error}>
              Could not load who owns each item, so nothing can be changed. Reload to try again.
            </span>
          )}
        </>
      )}
      {said && (
        <span className={styles.muted} role="status">
          {said}{" "}
          <button type="button" className={styles.link} onClick={() => setSaid(null)}>
            Dismiss
          </button>
        </span>
      )}
      {pending?.kind === "person" && (
        <NoteDialog
          {...dialogBase}
          heading={`Assign ${what} to ${o.nameOf(pending.assignee)}?`}
          helpText="Each item's history records the change, and one Slack message lists them. An item someone changed meanwhile is left as it is."
          submitLabel="Assign"
          withNote={false}
          onSubmit={() => sendAssign(pending.assignee)}
        />
      )}
      {pending?.kind === "clear" && (
        <NoteDialog
          {...dialogBase}
          heading={`Clear the assignment of ${what}?`}
          helpText="They will not be assigned to anyone. They move to Unassigned."
          submitLabel="Clear assignment"
          withNote={false}
          tone="danger"
          onSubmit={() => sendAssign(null)}
        />
      )}
      {pending?.kind === "inrange" && (
        <NoteDialog
          {...dialogBase}
          heading="Are you sure these are not Amazon asks?"
          helpText={`This list is what Amazon owes. Assigning ${what} to InRange says Amazon does not owe them. Each shows as pending InRange approval until InRange approves; one InRange does not approve goes back to whoever had it.`}
          fieldLabel="Why do you believe they are not Amazon asks?"
          placeholder="For example, who holds them"
          submitLabel="Assign to InRange"
          optional
          onSubmit={(why) => sendAssign(INRANGE, why)}
        />
      )}
      {pending?.kind === "resolved" && (
        <NoteDialog
          {...dialogBase}
          heading={`Mark ${what} as received?`}
          helpText="Tell InRange how they were given; the note goes on each item. Each shows as pending InRange approval until InRange approves. An item already waiting on InRange is left as it is."
          fieldLabel="How were they given? For example, when and how they were sent."
          requiredMessage="Please say how, so InRange can check it."
          placeholder="Sent with the network diagram to noaa@inrange.io on 12 Oct"
          submitLabel="Send to InRange"
          onSubmit={(text) =>
            resolve.mutate({ items: ids, text }, { onSuccess: finished("Marked as received:") })
          }
        />
      )}
    </div>
  );
};
